import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { GameState, Side } from "../../shared/game.ts";
import { useGameConnection, useLiveClocks, type ConnectionStatus } from "../hooks/useGameConnection.ts";
import { playGameSound, useGameSounds } from "../hooks/useGameSounds.ts";
import { usePushNotifications } from "../hooks/usePushNotifications.ts";
import { getGameState } from "../lib/api.ts";
import { formatClock, reasonLabel, resultTitle, sideLabel } from "../lib/format.ts";
import { getGameToken, getPlayerName, getSoundEnabled, setPlayerName, setSoundEnabled } from "../lib/storage.ts";
import { ChessBoard, PromotionPicker } from "./ChessBoard.tsx";
import { Icon } from "./Icon.tsx";
import { VoiceMessagePlayer, VoiceRecorder } from "./VoiceMessage.tsx";

type GameTab = "party" | "chat";
type PushController = ReturnType<typeof usePushNotifications>;

function opposite(side: Side): Side {
  return side === "white" ? "black" : "white";
}

function PlayerBar({
  side,
  state,
  clock,
}: {
  side: Side;
  state: GameState;
  clock: number;
}) {
  const player = state.players[side];
  const isYou = state.you === side;
  const timed = state.timeControl.initialSeconds > 0 && state.status !== "waiting";
  const activeClock = timed && state.status === "active" && state.turn === side;
  return (
    <div className={`player-bar ${activeClock ? "player-active" : ""} ${!player.joined ? "player-empty" : ""}`}>
      <div className={`player-avatar avatar-${side}`} aria-hidden="true">♟︎</div>
      <div className="player-meta">
        <div className="player-name-line">
          <strong>{player.name || "Свободное место"}</strong>
          {isYou && <span className="you-badge">Вы</span>}
        </div>
        <span className={`presence ${player.connected ? "presence-online" : ""}`}>
          <i />
          {!player.joined ? "Ждём игрока" : player.connected ? "В сети" : "Не в сети"}
        </span>
      </div>
      {timed && (
        <time className={`player-clock ${clock < 10_000 && activeClock ? "clock-low" : ""}`}>
          {formatClock(clock)}
        </time>
      )}
    </div>
  );
}

function ConnectionBadge({ status }: { status: ConnectionStatus }) {
  const online = status === "online";
  return (
    <span className={`connection-badge connection-${status}`}>
      <Icon name={online ? "wifi" : "wifi-off"} />
      {online ? "Связь есть" : status === "connecting" ? "Подключаемся…" : "Нет связи"}
    </span>
  );
}

function GameStatus({ state }: { state: GameState }) {
  let eyebrow = "Партия идёт";
  let title = "";
  let detail = "";
  if (state.status === "waiting") {
    const invited = !state.players.white.joined ? state.players.white.name : state.players.black.name;
    eyebrow = "Приглашение отправлено";
    title = invited ? `Ждём ${invited}` : "Ждём друга";
    detail = invited
      ? "Приглашение появилось у друга в приложении. Игра начнётся после принятия."
      : "Ожидаем, когда второй игрок присоединится.";
  } else if (state.status === "finished") {
    eyebrow = state.result ?? "Результат";
    title = state.finishReason === "cancelled" ? "Партия не состоится" : resultTitle(state.result, state.you);
    detail = reasonLabel(state.finishReason);
  } else if (state.you === "spectator") {
    eyebrow = "Вы смотрите партию";
    title = state.check ? `Шах. Ходят ${state.turn === "white" ? "белые" : "чёрные"}` : `Ходят ${state.turn === "white" ? "белые" : "чёрные"}`;
    detail = "Доска обновляется в реальном времени.";
  } else if (state.turn === state.you) {
    eyebrow = state.check ? "Ваш король под шахом" : "Партия идёт";
    title = state.check ? "Защититесь от шаха" : "Ваш ход";
    detail = "Коснитесь фигуры, затем нужной клетки.";
  } else {
    eyebrow = state.check ? "Шах сопернику" : "Партия идёт";
    title = "Ход соперника";
    detail = state.players[state.turn].connected ? "Ожидаем ответный ход." : "Соперник не в сети, но его место сохранено.";
  }
  return (
    <div className={`game-status status-${state.status}`} aria-live="polite">
      <span className="eyebrow">{eyebrow}</span>
      <strong>{title}</strong>
      <p>{detail}</p>
    </div>
  );
}

function MoveHistory({ state }: { state: GameState }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pairs = useMemo(() => {
    const rows: Array<{ number: number; white?: string; black?: string }> = [];
    for (const move of state.moves) {
      const index = Math.floor((move.ply - 1) / 2);
      const row = rows[index] ?? { number: index + 1 };
      if (move.ply % 2 === 1) row.white = move.san;
      else row.black = move.san;
      rows[index] = row;
    }
    return rows;
  }, [state.moves]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [state.moves.length]);

  return (
    <div className="moves-scroll" ref={scrollRef}>
      {pairs.length === 0 ? (
        <div className="moves-empty"><span className="empty-white-piece" aria-hidden="true">♟︎</span><p>Здесь появится запись партии.</p></div>
      ) : (
        <ol className="moves-list">
          {pairs.map((row) => (
            <li key={row.number}>
              <span>{row.number}.</span>
              <b>{row.white ?? ""}</b>
              <b>{row.black ?? ""}</b>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function PushPrompt({ push, player }: { push: PushController; player: boolean }) {
  const [dismissed, setDismissed] = useState(false);
  const visible = player && !dismissed && ["idle", "denied", "error"].includes(push.status);
  if (!visible) return null;

  const denied = push.status === "denied";
  return (
    <aside className={`push-prompt push-prompt-${push.status}`} aria-label="Включение уведомлений">
      <div className="push-prompt-icon"><Icon name="bell" /></div>
      <div className="push-prompt-copy">
        <strong>{denied ? "Пуши выключены" : "Не пропустите ход"}</strong>
        <p>{denied
          ? "Разрешите уведомления в настройках приложения, затем нажмите кнопку ещё раз."
          : "Сообщим, когда соперник сделает ход или напишет в чат."}</p>
      </div>
      <button className="button button-primary push-prompt-action" type="button" onClick={() => void push.enable()}>
        <Icon name="bell" /> {denied ? "Включить снова" : "Включить пуши"}
      </button>
      <button className="icon-button push-prompt-close" type="button" onClick={() => setDismissed(true)} aria-label="Напомнить позже">
        <Icon name="x" />
      </button>
    </aside>
  );
}

function ChatPanel({
  state,
  connection,
  onSend,
}: {
  state: GameState;
  connection: ConnectionStatus;
  onSend: (message: Record<string, unknown>) => boolean;
}) {
  const [text, setText] = useState("");
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messages = state.messages ?? [];
  const player = state.you !== "spectator";

  useEffect(() => {
    const scroller = scrollRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [messages.length]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const message = text.replace(/\s+/g, " ").trim();
    if (!message || !player) return;
    if (onSend({ type: "chat", text: message })) setText("");
  };

  return (
    <div className="chat-panel" role="tabpanel" id="chat-panel" aria-labelledby="chat-tab">
      <div className="chat-scroll" ref={scrollRef} role="log" aria-live="polite" aria-label="Сообщения игроков">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <Icon name="message" />
            <strong>{player ? "Напишите сопернику" : "Чат игроков"}</strong>
            <p>{player ? "Сообщения сохранятся вместе с партией." : "Переписка видна только участникам партии."}</p>
          </div>
        ) : (
          <ol className="chat-list">
            {messages.map((message) => {
              const own = message.side === state.you;
              return (
                <li key={message.id} className={`${own ? "chat-own" : "chat-other"} ${message.kind === "voice" ? "chat-voice" : ""}`}>
                  <span>{own ? "Вы" : state.players[message.side].name ?? "Соперник"}</span>
                  {message.kind === "voice"
                    ? <VoiceMessagePlayer gameId={state.id} messageId={message.id} durationMs={message.durationMs} />
                    : <p>{message.text}</p>}
                  <time dateTime={new Date(message.createdAt).toISOString()}>
                    {new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(message.createdAt)}
                  </time>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      {player ? (
        <div className="chat-composer">
          {voiceError && <p className="voice-error" role="alert">{voiceError}</p>}
          <form className={`chat-form ${voiceActive ? "chat-form-voice-active" : ""}`} onSubmit={submit}>
            {!voiceActive && (
              <input
                value={text}
                onChange={(event) => setText(event.target.value.slice(0, 300))}
                placeholder="Сообщение…"
                aria-label="Сообщение сопернику"
                autoComplete="off"
                enterKeyHint="send"
                disabled={connection !== "online"}
              />
            )}
            <VoiceRecorder
              gameId={state.id}
              disabled={connection !== "online"}
              onActiveChange={setVoiceActive}
              onError={setVoiceError}
            />
            {!voiceActive && (
              <button className="chat-text-send" type="submit" aria-label="Отправить сообщение" disabled={!text.trim() || connection !== "online"}>
                <Icon name="send" />
              </button>
            )}
          </form>
        </div>
      ) : (
        <div className="chat-spectator-note">Сообщения доступны только двум игрокам.</div>
      )}
    </div>
  );
}

function GameTabs({
  tab,
  unread,
  messageCount,
  onChange,
}: {
  tab: GameTab;
  unread: number;
  messageCount: number;
  onChange: (tab: GameTab) => void;
}) {
  return (
    <nav className="game-top-tabs" aria-label="Разделы партии">
      <div className="activity-tabs" role="tablist" aria-label="Партия и чат">
        <button
          id="party-tab"
          type="button"
          role="tab"
          aria-selected={tab === "party"}
          aria-controls="party-panel"
          className={tab === "party" ? "selected" : ""}
          onClick={() => onChange("party")}
        >
          Партия
        </button>
        <button
          id="chat-tab"
          type="button"
          role="tab"
          aria-selected={tab === "chat"}
          aria-controls="chat-panel"
          className={tab === "chat" ? "selected" : ""}
          onClick={() => onChange("chat")}
        >
          Чат {unread > 0
            ? <small className="unread-badge" aria-label={`${unread} непрочитанных`}>{unread}</small>
            : messageCount > 0
              ? <small>{messageCount}</small>
              : null}
        </button>
      </div>
    </nav>
  );
}

function GameWorkspace({
  state,
  connection,
  onSend,
  party,
  tab,
}: {
  state: GameState;
  connection: ConnectionStatus;
  onSend: (message: Record<string, unknown>) => boolean;
  party: ReactNode;
  tab: GameTab;
}) {
  return (
    <section className="activity-panel game-workspace">
      {tab === "party"
        ? <div className="party-panel" role="tabpanel" id="party-panel" aria-labelledby="party-tab">{party}</div>
        : <ChatPanel state={state} connection={connection} onSend={onSend} />}
    </section>
  );
}

function LoadingGame() {
  return (
    <main className="game-page loading-page">
      <div className="loading-brand"><span className="brand-mark">♞</span><span className="spinner spinner-dark" /></div>
      <p>Открываем партию…</p>
    </main>
  );
}

function JoinName({ gameId, onContinue }: { gameId: string; onContinue: (name: string) => void }) {
  const [name, setName] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const clean = name.replace(/\s+/g, " ").trim();
    if (!clean) return;
    setPlayerName(clean);
    onContinue(clean);
  };
  return (
    <main className="join-page">
      <section className="join-card">
        <a className="brand" href="/">
          <span className="brand-mark" aria-hidden="true">♞</span>
          <span>Шахматы</span>
        </a>
        <div className="join-invite-art" aria-hidden="true"><span>♚</span><i /><span>♚</span></div>
        <span className="eyebrow">Вас пригласили в партию</span>
        <h1>Как вас представить?</h1>
        <p>Имя увидит соперник. Мы запомним его для следующих партий.</p>
        <form onSubmit={submit}>
          <label className="field-label" htmlFor="guest-name">Ваше имя</label>
          <div className="input-wrap">
            <Icon name="user" />
            <input
              id="guest-name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 24))}
              placeholder="Например, Михаил"
              autoComplete="name"
              autoFocus
              enterKeyHint="go"
            />
          </div>
          <button className="button button-primary button-large button-full" type="submit" disabled={!name.trim()}>
            Занять место
            <Icon name="chevron" />
          </button>
        </form>
      </section>
    </main>
  );
}

export function GamePage({ gameId }: { gameId: string }) {
  const [playerName, setLocalPlayerName] = useState(
    () => getPlayerName() || (getGameToken(gameId) ? "Игрок" : ""),
  );
  const [checkingSeat, setCheckingSeat] = useState(() => !getPlayerName() && !getGameToken(gameId));
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const game = useGameConnection(gameId, playerName || null);
  const clocks = useLiveClocks(game.state, game.receivedAt);
  const playerForPush = Boolean(game.state && game.state.you !== "spectator");
  const push = usePushNotifications(gameId, playerForPush);
  const [orientation, setOrientation] = useState<Side>("white");
  const orientationInitialized = useRef(false);
  const [promotion, setPromotion] = useState<{ from: string; to: string } | null>(null);
  const [confirmResign, setConfirmResign] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<GameTab>("party");
  const [soundEnabled, setSoundEnabledState] = useState(getSoundEnabled);
  const [seenMessageId, setSeenMessageId] = useState(0);
  const trackedMessagesForGame = useRef<string | null>(null);
  const previousMoveCount = useRef(0);
  const latestMessageId = game.state?.messages?.at(-1)?.id ?? 0;

  useGameSounds(game.state, soundEnabled);

  useEffect(() => {
    if (!game.state) return;
    if (trackedMessagesForGame.current !== game.state.id) {
      trackedMessagesForGame.current = game.state.id;
      setSeenMessageId(latestMessageId);
      return;
    }
    if (tab === "chat") setSeenMessageId(latestMessageId);
  }, [game.state?.id, latestMessageId, tab]);

  useEffect(() => {
    if (!checkingSeat || playerName) return;
    let cancelled = false;
    void getGameState(gameId)
      .then((publicState) => {
        if (cancelled) return;
        if (publicState.players.white.joined && publicState.players.black.joined) {
          setLocalPlayerName("Зритель");
        }
        setCheckingSeat(false);
      })
      .catch((error) => {
        if (!cancelled) {
          setPreflightError(error instanceof Error ? error.message : "Не удалось открыть партию.");
          setCheckingSeat(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [checkingSeat, gameId, playerName]);

  useEffect(() => {
    if (!game.state || orientationInitialized.current) return;
    if (game.state.you !== "spectator") setOrientation(game.state.you);
    orientationInitialized.current = true;
  }, [game.state]);

  useEffect(() => {
    if (!game.state) return;
    document.title = game.state.status === "waiting" ? "Ожидаем друга — Шахматы" : "Шахматы";
    if (game.state.moves.length > previousMoveCount.current && previousMoveCount.current > 0) {
      navigator.vibrate?.(18);
    }
    previousMoveCount.current = game.state.moves.length;
  }, [game.state]);

  useEffect(() => {
    if (!game.actionError) return;
    setToast(game.actionError);
    game.clearActionError();
  }, [game.actionError, game.clearActionError]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (checkingSeat && !playerName) return <LoadingGame />;
  if (preflightError && !playerName) {
    return (
      <main className="centered-page">
        <section className="error-card">
          <div className="brand-mark" aria-hidden="true">♚</div>
          <span className="eyebrow">Не получилось открыть</span>
          <h1>{preflightError}</h1>
          <p>Проверьте ссылку или создайте новую игру.</p>
          <a className="button button-primary" href="/">На главную</a>
        </section>
      </main>
    );
  }
  if (!playerName) return <JoinName gameId={gameId} onContinue={setLocalPlayerName} />;
  if (game.loading) return <LoadingGame />;
  if (game.fatalError || !game.state) {
    return (
      <main className="centered-page">
        <section className="error-card">
          <div className="brand-mark" aria-hidden="true">♚</div>
          <span className="eyebrow">Не получилось открыть</span>
          <h1>{game.fatalError || "Партия не найдена"}</h1>
          <p>Проверьте ссылку или создайте новую игру.</p>
          <a className="button button-primary" href="/">На главную</a>
        </section>
      </main>
    );
  }

  const state = game.state;
  const playerSide = state.you === "spectator" ? null : state.you;
  const topSide = opposite(orientation);
  const bottomSide = orientation;
  const opponentOffer = Boolean(playerSide && state.drawOfferBy && state.drawOfferBy !== playerSide);
  const ownOffer = Boolean(playerSide && state.drawOfferBy === playerSide);
  const messages = state.messages ?? [];
  const unread = messages.filter((message) => message.id > seenMessageId && message.side !== state.you).length;

  const chooseMove = (from: string, to: string, promotionOptions: string[]) => {
    if (promotionOptions.length) setPromotion({ from, to });
    else game.makeMove(from, to);
  };

  const confirmPromotion = (piece: string) => {
    if (promotion) game.makeMove(promotion.from, promotion.to, piece);
    setPromotion(null);
  };

  const selectTab = (nextTab: GameTab) => {
    setTab(nextTab);
    if (window.innerWidth <= 760) {
      window.requestAnimationFrame(() => window.scrollTo({ top: 0 }));
    }
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabledState(next);
    setSoundEnabled(next);
    if (next) void playGameSound("enabled");
  };

  return (
    <main className="game-page">
      <header className="game-header">
        <a className="icon-button" href="/" aria-label="На главную"><Icon name="arrow-left" /></a>
        <a className="brand brand-compact" href="/"><span className="brand-mark" aria-hidden="true">♞</span><span>Шахматы</span></a>
        <div className="game-header-actions">
          <button
            className={`icon-button sound-toggle ${soundEnabled ? "sound-on" : "sound-off"}`}
            type="button"
            onClick={toggleSound}
            aria-label={soundEnabled ? "Выключить звуки" : "Включить звуки"}
            aria-pressed={soundEnabled}
          >
            <Icon name={soundEnabled ? "volume" : "volume-off"} />
          </button>
          <ConnectionBadge status={game.connection} />
        </div>
      </header>

      <GameTabs
        tab={tab}
        unread={unread}
        messageCount={messages.length}
        onChange={selectTab}
      />

      <PushPrompt push={push} player={Boolean(playerSide)} />

      <div className={`game-layout game-layout-${tab}`}>
        <section className="board-column">
          <PlayerBar side={topSide} state={state} clock={clocks[topSide]} />
          <ChessBoard
            fen={state.fen}
            orientation={orientation}
            playerSide={playerSide}
            turn={state.turn}
            active={state.status === "active" && game.connection === "online"}
            check={state.check}
            lastMove={state.lastMove}
            onMove={chooseMove}
          />
          <PlayerBar side={bottomSide} state={state} clock={clocks[bottomSide]} />
          <div className="board-tools">
            <button className="button button-small button-quiet" type="button" onClick={() => setOrientation(opposite(orientation))}>
              <Icon name="rotate" /> Перевернуть доску
            </button>
          </div>
        </section>

        <aside className="game-sidebar">
          <GameWorkspace
            state={state}
            connection={game.connection}
            onSend={game.send}
            tab={tab}
            party={(
              <>
                <GameStatus state={state} />

                {state.status === "waiting" && (
                  <div className="invite-card waiting-friend-card">
                    <div className="invite-icon"><Icon name="user" /></div>
                    <div>
                      <strong>{(!state.players.white.joined ? state.players.white.name : state.players.black.name) || "Ожидаем второго игрока"}</strong>
                      <p>Можно закрыть приложение — мы пришлём уведомление, когда друг примет приглашение.</p>
                    </div>
                  </div>
                )}

                {opponentOffer && (
                  <div className="draw-offer" role="alert">
                    <span className="draw-icon"><Icon name="handshake" /></span>
                    <div><strong>Соперник предлагает ничью</strong><p>Завершить партию без победителя?</p></div>
                    <div className="draw-buttons">
                      <button className="button button-primary" type="button" onClick={() => game.send({ type: "accept_draw" })}><Icon name="check" /> Принять</button>
                      <button className="button button-quiet" type="button" onClick={() => game.send({ type: "decline_draw" })}><Icon name="x" /> Отклонить</button>
                    </div>
                  </div>
                )}

                <section className="party-moves" aria-labelledby="moves-heading">
                  <div className="party-section-heading">
                    <h3 id="moves-heading">Ходы</h3>
                    {state.moves.length > 0 && <span>{state.moves.length}</span>}
                  </div>
                  <MoveHistory state={state} />
                </section>

                <div className="game-actions">
                  {state.status === "active" && playerSide && (
                    <>
                      <button className="button button-quiet" type="button" disabled={ownOffer} onClick={() => game.send({ type: "offer_draw" })}>
                        <Icon name="handshake" /> {ownOffer ? "Ничья предложена" : "Предложить ничью"}
                      </button>
                      <button className="button button-danger-quiet" type="button" onClick={() => setConfirmResign(true)}>
                        <Icon name="flag" /> Сдаться
                      </button>
                    </>
                  )}
                  {state.status === "finished" && (
                    <a className="button button-primary" href="/"><Icon name="plus" /> Новая партия</a>
                  )}
                  {state.you === "spectator" && state.status !== "finished" && (
                    <div className="spectator-note"><Icon name="eye" /><span><strong>Режим зрителя</strong>Оба места уже заняты.</span></div>
                  )}
                </div>
              </>
            )}
          />
        </aside>
      </div>

      {promotion && playerSide && (
        <PromotionPicker side={playerSide} onChoose={confirmPromotion} onCancel={() => setPromotion(null)} />
      )}

      {confirmResign && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setConfirmResign(false)}>
          <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="resign-title">
            <div className="modal-symbol danger-symbol"><Icon name="flag" /></div>
            <span className="eyebrow">Завершение партии</span>
            <h2 id="resign-title">Точно сдаться?</h2>
            <p>Сопернику будет засчитана победа. Это действие нельзя отменить.</p>
            <div className="modal-actions">
              <button className="button button-danger" type="button" onClick={() => { game.send({ type: "resign" }); setConfirmResign(false); }}>Сдаться</button>
              <button className="button button-quiet" type="button" onClick={() => setConfirmResign(false)}>Продолжить игру</button>
            </div>
          </section>
        </div>
      )}

      {toast && <div className="toast" role="status"><Icon name="check" />{toast}</div>}
    </main>
  );
}
